import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { SalvButton, Size } from '@salvapp/ui-components';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { customListApi } from '../apis';
import { useNavigate } from 'react-router';
import Has from '../auth/Has';
import { CAN_ADD_CUSTOM_LIST, CAN_MODIFY_CUSTOM_LIST, CAN_READ_CUSTOM_LIST } from '../auth/permissions';
import CustomListInfoSection from './CustomListInfoSection';
import { InternalApiCustomList } from '../api-internal';
import { fetchCustomListSuccess } from './customListsReducer';
import { useDispatch } from 'react-redux';

export interface CustomListInfoFormValues {
  listName: string;
  code?: string;
  description: string;
}

export const CustomListInfo = ({ customList }: { customList?: InternalApiCustomList }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const initialValues: CustomListInfoFormValues = {
    listName: customList?.name || '',
    description: customList?.description || '',
    code: customList?.code || '',
  };
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setEditing(!customList);
  }, [customList]);

  const validationShape = {
    listName: Yup.string().required('Required'),
    description: Yup.string().required('Required'),
    code: Yup.string(),
  };

  if (!customList) {
    validationShape.code = Yup.string()
      .matches(/^\w+$/i, {
        message: 'Code must contain letters, numbers or underscores',
      })
      .required('Required');
  }

  const validationSchema: Yup.Schema<CustomListInfoFormValues> = Yup.object(validationShape);

  const submit = async (
    values: CustomListInfoFormValues,
    { setSubmitting }: FormikHelpers<CustomListInfoFormValues>
  ) => {
    try {
      setSubmitting(true);
      if (customList) {
        await customListApi.updateList({
          customListId: customList.id!,
          internalApiCustomList: {
            name: values.listName,
            description: values.description,
          },
        });
      } else {
        const list = await customListApi.addList({
          internalApiCustomList: {
            name: values.listName,
            description: values.description,
            code: values.code,
          },
        });
        dispatch(fetchCustomListSuccess(list));
        navigate(`/custom-lists/${list.id}/settings`);
      }
      setEditing(false);
    } catch (_) {
      // eslint-disable-next-line
      console.log('Something went wrong');
    }
    setSubmitting(false);
  };

  return (
    <Card body={false} className='card-info'>
      <Card.Header>
        <div className='title-s'>General</div>
        {customList?.canEdit && (
          <Has anyPermissions={[CAN_MODIFY_CUSTOM_LIST]}>
            {!editing && (
              <SalvButton
                label={'Edit general info'}
                size={Size.SMALL}
                variant='muted'
                onClick={() => setEditing(true)}
              />
            )}
          </Has>
        )}
      </Card.Header>
      <Card.Body>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={submit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, values, setFieldValue, resetForm }: FormikProps<CustomListInfoFormValues>) => {
            return (
              <Form>
                <div
                  style={{
                    width: '500px',
                  }}
                >
                  <CustomListInfoSection
                    editing={editing}
                    name='listName'
                    value={values.listName}
                    label='List name'
                    setFieldValue={setFieldValue}
                    className={'m-b-24'}
                  />
                  <CustomListInfoSection
                    editing={editing}
                    name='code'
                    value={values.code}
                    label='Code'
                    setFieldValue={async (name: string, code: string) => {
                      await setFieldValue(name, code.toUpperCase());
                    }}
                    disabled={!!customList}
                    className={'m-b-24'}
                  />
                  <CustomListInfoSection
                    editing={editing}
                    name='description'
                    value={values.description}
                    label='Description'
                    setFieldValue={setFieldValue}
                  />
                </div>

                <Has anyPermissions={[CAN_ADD_CUSTOM_LIST, CAN_READ_CUSTOM_LIST]}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {customList ? (
                      <>
                        {customList.canEdit && editing && (
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                            }}
                          >
                            <SalvButton
                              label='Cancel'
                              variant='muted'
                              onClick={() => {
                                resetForm();
                                setEditing(false);
                              }}
                            />
                            <SalvButton label='Save' type='submit' variant='primary' disabled={isSubmitting} />
                          </div>
                        )}
                      </>
                    ) : (
                      <SalvButton label='Add' type='submit' variant='primary' disabled={isSubmitting} />
                    )}
                  </div>
                </Has>
              </Form>
            );
          }}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default CustomListInfo;
